import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { trackExternalLink, trackNavigation } from '../utils/analytics';
import HeroVisual from './HeroVisual';

export default function Hero() {
  const { t } = useTranslation();

  const handleHeroLinkClick = (platform: string, url: string) => {
    trackExternalLink(platform, url, 'hero');
  };

  const handleCtaClick = (label: string, url: string) => {
    trackNavigation(label, url, 'hero');
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center px-4 py-4 overflow-hidden bg-linear-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 noise-texture">
      {/* Ambient background accents */}
      <div className="absolute inset-0 bg-radial-accent pointer-events-none" aria-hidden="true" />
      <div
        className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-blue-400/10 dark:bg-blue-500/10 blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-1/4 -right-32 w-80 h-80 rounded-full bg-blue-300/10 dark:bg-blue-400/8 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="container mx-auto max-w-6xl px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center lg:text-left"
          >
            <p className="section-label mb-4">
              {t('hero.subtitle')}
            </p>

            <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-gray-900 dark:text-white mb-5 leading-[1.1]">
              {t('hero.welcome')}
            </h1>

            <p className="text-xl md:text-2xl text-gray-800 dark:text-gray-100 mb-4 max-w-xl mx-auto lg:mx-0 font-medium leading-snug">
              {t('hero.description')}
            </p>

            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              {t('hero.supporting')}
            </p>

            {/* Primary calls to action */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
              <Link
                to="/projects"
                className="button__primary"
                onClick={() => handleCtaClick('View projects', '/projects')}
              >
                {t('hero.cta.viewProjects')}
                <ArrowRightIcon className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="button__outline"
                onClick={() => handleCtaClick('Contact me', '/contact')}
              >
                {t('hero.cta.contact')}
              </Link>
            </div>

            {/* Social Links */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <a
                href="https://github.com/jsaq007"
                target="_blank"
                rel="noopener noreferrer"
                className="button__outline"
                tabIndex={0}
                onClick={() => handleHeroLinkClick('GitHub', 'https://github.com/jsaq007')}
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                GitHub
              </a>

              <a
                href="https://www.linkedin.com/in/julian-querido/"
                target="_blank"
                rel="noopener noreferrer"
                className="button__outline"
                tabIndex={0}
                onClick={() => handleHeroLinkClick('LinkedIn', 'https://www.linkedin.com/in/julian-querido/')}
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
                LinkedIn
              </a>
            </div>
          </motion.div>

          {/* Hero visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative hidden sm:block"
          >
            <HeroVisual />
          </motion.div>
        </div>

        {/* SEO summary — visible, minimal text for crawlers and first paint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 md:mt-20 max-w-3xl mx-auto text-center border-t border-gray-200/60 dark:border-gray-700/60 pt-10"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
            {t('hero.seoSummary1')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
            {t('hero.seoSummary2')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            {t('hero.seoSummary3')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
